import { mutationOptions } from "@tanstack/react-query";
import { type DbClient } from "@tanstack/db";
import { type QueryClient } from "@tanstack/query-core";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import { collectionError } from "@softmax/web-features/data";
import { myPlayersCollection, myPlayersKey } from "./player.collections";
import { isUncertainWrite } from "@/features/account/state";
import {
  performPlayerAction,
  type PlayerAction,
} from "./perform-player-action";

export function playerMutationOptions({
  dbClient,
  viewerId,
  elevatedPrivileges,
}: {
  dbClient: DbClient;
  viewerId: string;
  elevatedPrivileges: boolean;
}) {
  const key = myPlayersKey({ viewerId, elevatedPrivileges });
  const queryClient = dbClient.requireDependency<QueryClient>("queryClient");
  const signal = dbClient.requireDependency<AbortController>("lifetime").signal;
  const source = dbClient.collection(
    myPlayersCollection({ viewerId, elevatedPrivileges }),
  );
  const options = { elevatedPrivileges, signal };
  const relatedKeys = [
    ["policy-evidence"],
    ["league-policy-memberships"],
    ["policy-versions"],
    ["league-submissions"],
    ["divisions"],
    ["leagues", "participation"],
    ["competition-events"],
    ["admin-users-report"],
    ["posts"],
  ];
  return mutationOptions({
    mutationFn: (action: PlayerAction) =>
      performPlayerAction({ action, source, options }),
    onMutate: () => {
      signal.throwIfAborted();
      return queryClient.cancelQueries({ queryKey: key, exact: true });
    },
    onSuccess: async (player, action) => {
      signal.throwIfAborted();
      const initialStatus = source.status;

      if (initialStatus === "cleaned-up") return;

      await queryClient.cancelQueries({ queryKey: key, exact: true });
      signal.throwIfAborted();

      if (
        source.status === "ready" &&
        !requestUnavailable(collectionError(source.health))
      ) {
        if (action.kind === "create") await source.utils.writeUpsert(player);

        signal.throwIfAborted();

        if (action.kind === "upload-avatar" || action.kind === "remove-avatar")
          await source.utils.writeUpdate({
            id: player.id,
            avatar_url: player.avatar_url ?? null,
          });

        signal.throwIfAborted();

        if (
          action.kind === "default" ||
          (action.kind === "create" && player.is_default)
        )
          await source.utils.writeUpdate(
            Array.from(source.values(), (row) => ({
              id: row.id,
              is_default: row.id === player.id,
            })),
          );
      }

      signal.throwIfAborted();

      if (source.status === "cleaned-up") return;

      await Promise.all(
        relatedKeys.map((queryKey) => queryClient.cancelQueries({ queryKey })),
      );
      signal.throwIfAborted();
      const refresh =
        source.status === "idle"
          ? source.preload()
          : source.utils.refetch({ throwOnError: true });
      void refresh.catch(() => undefined);
      void Promise.all(
        relatedKeys.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
    onError: (error) => {
      if (signal.aborted || source.status === "cleaned-up") return;

      if (requestUnavailable(error) || isUncertainWrite(error))
        void source.utils
          .refetch({ throwOnError: true })
          .catch(() => undefined);
    },
    retry: false,
  });
}
